/**
 * The sign-up rules, mirrored from the server so the form rejects, field by field, everything the
 * server would reject, BEFORE any request is made.
 *
 * Source of truth: `insight-hub-server/src/schemas/user.signup.ts` (Joi). Keep the two in step:
 * `test/fixtures/signup-validation-cases.json` (a copy lives in both repos) is checked against the
 * real server schema in the backend and against these rules here, so a change on one side alone fails a test.
 *
 * Each field yields at most one message: the first rule it breaks, written for the person filling the form.
 */

export type SignUpField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'gender'
  | 'country'
  | 'phone'
  | 'companyName'
  | 'jobTitle'
  | 'password'
  | 'confirmPassword';

export type SignUpFieldErrors = Partial<Record<SignUpField, string>>;

/** Order used to list errors and to focus the first invalid field. */
export const SIGN_UP_FIELD_ORDER: SignUpField[] = [
  'firstName',
  'lastName',
  'email',
  'gender',
  'country',
  'phone',
  'companyName',
  'jobTitle',
  'password',
  'confirmPassword',
];

// ── Server rules (user.signup.ts) ────────────────────────────────────────────
export const NAME_PATTERN = /^[A-Za-z\s-]+$/; // first and last name
export const ORG_TEXT_PATTERN = /^[A-Za-z0-9\s-]+$/; // company and job title
export const ORG_TEXT_MIN = 3;
export const ORG_TEXT_MAX = 50;
export const PASSWORD_MIN = 6;
export const PASSWORD_SPECIALS = '!@#$%^&*'; // the ONLY special characters the server accepts
const PASSWORD_SPECIAL_PATTERN = /[!@#$%^&*]/;
// International format: '+' and 8 to 15 digits (the server allows 2 to 15; the form is stricter on purpose).
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;
// Characters the form has always removed from passwords before sending; now rejected instead of silently changed.
const PASSWORD_FORBIDDEN = /[<>"']/;
// Close to what the server's email check accepts: one @, no spaces, no empty or dotted-over labels, a letters-only ending.
const EMAIL_PATTERN = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@([A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export interface SignUpValues {
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  country: string;
  countryCode: string;
  phone: string;
  password: string;
  confirmPassword: string;
  companyName?: string;
  jobTitle?: string;
}

const nameError = (label: string, value: string): string | undefined => {
  const text = value.trim();
  if (!text) return `Enter your ${label.toLowerCase()}.`;
  if (!NAME_PATTERN.test(text)) {
    return `${label} can only contain letters, spaces and hyphens. Please remove apostrophes, accents, numbers and symbols.`;
  }
  return undefined;
};

const orgTextError = (label: string, value: string): string | undefined => {
  const text = value.trim();
  if (!text) return undefined; // optional
  if (!ORG_TEXT_PATTERN.test(text)) {
    return `${label} can only contain letters, numbers, spaces and hyphens. Please remove apostrophes, dots and other symbols.`;
  }
  if (text.length < ORG_TEXT_MIN) return `${label} must be at least ${ORG_TEXT_MIN} characters, or leave it blank.`;
  if (text.length > ORG_TEXT_MAX) return `${label} must be ${ORG_TEXT_MAX} characters or fewer.`;
  return undefined;
};

/** Phone numbers may be typed with spaces, dashes or brackets; only digits and '+' are kept. */
export const cleanPhone = (value: string): string => value.trim().replace(/[^0-9+]/g, '');

const phoneError = (value: string): string | undefined => {
  // Separators are fine, but letters and symbols are refused (they used to be deleted silently, changing the number).
  if (/[^0-9+\s().-]/.test(value.trim())) {
    return 'Phone number can only contain digits, spaces and dashes, with a + at the start. Please remove any letters or symbols.';
  }
  const phone = cleanPhone(value);
  if (!phone || phone === '+') return 'Enter your phone number.';
  if (!phone.startsWith('+')) return 'Start your phone number with your country code, like +233 for Ghana.';
  if (!PHONE_PATTERN.test(phone)) {
    return 'Enter a valid international phone number, for example +233241234567 (8 to 15 digits after the +).';
  }
  return undefined;
};

const passwordError = (value: string): string | undefined => {
  if (!value.trim()) return 'Create a password.';
  if (PASSWORD_FORBIDDEN.test(value)) return 'Your password can\'t contain the characters < > " or \'. Please remove them.';
  if (value.trim().length < PASSWORD_MIN) return `Your password must be at least ${PASSWORD_MIN} characters long.`;
  if (!/[A-Z]/.test(value)) return 'Add at least one capital letter to your password.';
  if (!/[a-z]/.test(value)) return 'Add at least one lowercase letter to your password.';
  if (!/[0-9]/.test(value)) return 'Add at least one number to your password.';
  if (!PASSWORD_SPECIAL_PATTERN.test(value)) {
    return `Add at least one of these special characters to your password: ${PASSWORD_SPECIALS.split('').join(' ')}. Other symbols such as . ? ( ) aren't accepted.`;
  }
  return undefined;
};

/**
 * Checks every field against the server's rules.
 *
 * @param values - What the person typed (untrimmed).
 * @returns One message per invalid field; an empty object means every field is acceptable.
 */
export const validateSignUpFields = (values: SignUpValues): SignUpFieldErrors => {
  const errors: SignUpFieldErrors = {};

  const set = (field: SignUpField, message: string | undefined): void => {
    if (message) errors[field] = message;
  };

  set('firstName', nameError('First name', values.firstName));
  set('lastName', nameError('Last name', values.lastName));

  const email = values.email.trim();
  if (!email) set('email', 'Enter your email address.');
  else if (!EMAIL_PATTERN.test(email)) set('email', 'Enter a valid email address, like name@example.com.');

  if (!['male', 'female', 'rather_not_say'].includes(values.gender.trim().toLowerCase())) {
    set('gender', 'Choose an option for gender.');
  }

  const country = values.country.trim().toLowerCase();
  if (!country || country === 'select your country' || values.countryCode.trim().length !== 2) {
    set('country', 'Choose your country.');
  }

  set('phone', phoneError(values.phone));
  set('companyName', orgTextError('Company name', values.companyName ?? ''));
  set('jobTitle', orgTextError('Job title', values.jobTitle ?? ''));

  const passwordProblem = passwordError(values.password);
  set('password', passwordProblem);
  if (!values.confirmPassword.trim()) set('confirmPassword', 'Confirm your password.');
  else if (values.confirmPassword !== values.password) set('confirmPassword', 'The two passwords don\'t match.');

  return errors;
};

// ── Later steps: the same rules, applied again to what step 1 handed over ────
const GENDER_KEY: Record<string, string> = {
  male: 'male',
  female: 'female',
  'rather not say': 'rather_not_say',
  rather_not_say: 'rather_not_say',
};

/** Step-1 values as they travel in the router state (already cleaned; gender is the display label). */
export interface SignUpStateValues {
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  gender?: string | undefined;
  country?: string | undefined;
  countryCode?: string | undefined;
  phone?: string | undefined;
  password?: string | undefined;
  confirmPassword?: string | undefined;
  companyName?: string | undefined;
  jobTitle?: string | undefined;
}

/**
 * Checks the values a later step received from step 1 against the same rules. Steps 2 and 3 use this so
 * nobody can reach them (or trigger the sign-up request) with details that step 1 would have refused.
 */
export const revalidateSignUpState = (state: SignUpStateValues): SignUpFieldErrors =>
  validateSignUpFields({
    firstName: state.firstName ?? '',
    lastName: state.lastName ?? '',
    email: state.email ?? '',
    gender: GENDER_KEY[(state.gender ?? '').trim().toLowerCase()] ?? '',
    country: state.country ?? '',
    countryCode: state.countryCode ?? '',
    phone: state.phone ?? '',
    password: state.password ?? '',
    confirmPassword: state.confirmPassword ?? '',
    companyName: state.companyName ?? '',
    jobTitle: state.jobTitle ?? '',
  });

const titleCase = (value: string): string =>
  value.replace(/(^|[\s-])([a-z])/g, (_match, lead: string, letter: string) => `${lead}${letter.toUpperCase()}`);

/** What to put back in step 1 when someone is sent back to fix their details. Never includes the passwords. */
export const prefillFromState = (state: SignUpStateValues): Record<string, string> => ({
  firstName: titleCase(state.firstName ?? ''),
  lastName: titleCase(state.lastName ?? ''),
  email: state.email ?? '',
  gender: GENDER_KEY[(state.gender ?? '').trim().toLowerCase()] ?? '',
  country: titleCase(state.country ?? ''),
  countryCode: state.countryCode ?? '',
  phone: state.phone ?? '',
  companyName: titleCase(state.companyName ?? ''),
  jobTitle: titleCase(state.jobTitle ?? ''),
});
